import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RaffleResultWinner } from "@/apps/raffle/types";
import {
  formatRaffleFinishedAt,
  formatRaffleScheduledDrawTime,
  formatMoney,
  RUBLE_SIGN,
} from "@/apps/raffle/pages/RafflePage/utils";
import { useRafflePageContext } from "@/apps/raffle/pages/RafflePage/RafflePageContext";
import { GiftIcon } from "@/apps/raffle/pages/RafflePage/icons";
import { RaffleTimeIcon } from "@/apps/raffle/pages/RafflePage/RaffleTimer";
import {
  requestRaffleMulticFinished,
  requestRaffleMulticStarted,
} from "@/apps/raffle/services/raffleApi";
import { useRaffleDataStore } from "@/apps/raffle/store/raffleDataStore";
import { useStartupParamsStore } from "@/store/startupParams";
import { getWinnerKey, getWinnerSpinTarget } from "./resultsAnimationUtils";
import { useRaffleConfetti } from "./useRaffleConfetti";
import cls from "./styles.module.scss";

const SPIN_WAIT_MS = 4000;
const EJECT_DURATION_MS = 3000;
const COUNTDOWN_STEP_MS = 700;

const formatPrize = (amount: number) => `${formatMoney(amount)} ${RUBLE_SIGN}`;

type TimerEntry = {
  id: number;
  resolve: () => void;
};

interface RaffleResultsEjectScreenProps {
  autoStart?: boolean;
}

export const RaffleResultsEjectScreen = ({
  autoStart = true,
}: RaffleResultsEjectScreenProps) => {
  const { t } = useTranslation();
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const { participants, raffle, results, resultsSeconds, setResultsAnimation } =
    useRafflePageContext();
  const [flyChip, setFlyChip] = useState<RaffleResultWinner | null>(null);
  const [revealedWinners, setRevealedWinners] = useState<RaffleResultWinner[]>(
    []
  );
  const [countdownValue, setCountdownValue] = useState<string | null>(null);
  const [status, setStatus] = useState(t("raffle.resultsAnimation.waiting"));
  const [isAnimationStarted, setIsAnimationStarted] = useState(false);
  const timerRefs = useRef<TimerEntry[]>([]);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const rotationRef = useRef(0);
  const cancelledRef = useRef(false);
  const hasRunRef = useRef(false);
  const hasFinishedRef = useRef(false);
  const setBonusWinPopup = useRaffleDataStore(
    (state) => state.setBonusWinPopup
  );
  const { canvasRef, celebrateWinner, celebrateFinale } = useRaffleConfetti();
  const isFinishedStatus = Boolean(raffle.drawn_at && resultsSeconds === 0);
  const defaultTimerText = (() => {
    if (isFinishedStatus) {
      return t("raffle.timer.finishedAt", {
        date: formatRaffleFinishedAt(raffle.scheduled_draw_at),
      });
    }

    return t("raffle.timer.drawScheduledAt", {
      time: formatRaffleScheduledDrawTime(raffle.scheduled_draw_at),
    });
  })();
  const statusText = !isAnimationStarted ? defaultTimerText : status;

  const clearTimers = (resolvePending = false) => {
    timerRefs.current.forEach(({ id, resolve }) => {
      window.clearTimeout(id);

      if (resolvePending) {
        resolve();
      }
    });
    timerRefs.current = [];
  };

  const wait = (duration: number) =>
    new Promise<void>((resolve) => {
      if (cancelledRef.current) {
        resolve();
        return;
      }

      const timerId = window.setTimeout(() => {
        timerRefs.current = timerRefs.current.filter(
          (timer) => timer.id !== timerId
        );
        resolve();
      }, duration);

      timerRefs.current.push({
        id: timerId,
        resolve,
      });
    });

  const getStageCenter = () => {
    const rect = stageRef.current?.getBoundingClientRect();
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    const offsetX = canvasRect && rect ? rect.left - canvasRect.left : 0;
    const offsetY = canvasRect && rect ? rect.top - canvasRect.top : 0;

    return {
      x: offsetX + (rect?.width ?? window.innerWidth) * 0.5,
      y: offsetY + (rect?.height ?? window.innerHeight) * 0.5,
    };
  };

  const runCountdown = async () => {
    for (const value of ["3", "2", "1"]) {
      if (cancelledRef.current) return;

      setCountdownValue(value);
      await wait(COUNTDOWN_STEP_MS);
    }

    setCountdownValue(null);
  };

  const spinToWinner = async (
    winner: RaffleResultWinner,
    winnerIndex: number
  ) => {
    const spinTarget = getWinnerSpinTarget(
      participants,
      winner,
      rotationRef.current
    );

    if (!spinTarget) return;

    rotationRef.current = spinTarget.rotation;
    setResultsAnimation({
      activeParticipantIndex: spinTarget.targetIndex,
      rotation: spinTarget.rotation,
    });
    setStatus(
      t("raffle.resultsAnimation.drawing", {
        prize: formatPrize(winner.prize_amount),
        current: winnerIndex + 1,
        total: results?.winners.length ?? 0,
      })
    );
    await wait(SPIN_WAIT_MS);
  };

  const finishMultic = async () => {
    if (hasFinishedRef.current || !yandexId) return;

    hasFinishedRef.current = true;

    try {
      const finishResponse = await requestRaffleMulticFinished(
        raffle.id,
        yandexId
      );

      if (finishResponse.bonus_win_popup) {
        setBonusWinPopup(finishResponse.bonus_win_popup);
      }
    } catch (error) {
      console.error("Failed to mark raffle multic as finished", error);
    }
  };

  const completeAnimation = async () => {
    setResultsAnimation({
      activeParticipantIndex: undefined,
      rotation: rotationRef.current,
    });
    setStatus(t("raffle.resultsAnimation.finished"));
    celebrateFinale();
    await finishMultic();
  };

  const skipAnimation = async () => {
    if (!results || !isAnimationStarted || hasFinishedRef.current) return;

    cancelledRef.current = true;
    clearTimers(true);
    setCountdownValue(null);
    setFlyChip(null);
    setRevealedWinners(results.winners);
    await completeAnimation();
  };

  const run = async () => {
    if (hasRunRef.current || !results || results.winners.length === 0) return;

    hasRunRef.current = true;
    cancelledRef.current = false;
    setIsAnimationStarted(true);
    setRevealedWinners([]);
    setStatus(t("raffle.resultsAnimation.starting"));

    if (yandexId) {
      try {
        await requestRaffleMulticStarted(raffle.id, yandexId);
      } catch (error) {
        console.error("Failed to mark raffle multic as started", error);
      }
    }

    if (cancelledRef.current) return;

    for (let index = 0; index < results.winners.length; index += 1) {
      if (cancelledRef.current) return;

      const winner = results.winners[index];

      await runCountdown();
      if (cancelledRef.current) return;

      await spinToWinner(winner, index);
      if (cancelledRef.current) return;

      const stageCenter = getStageCenter();
      setStatus(
        t("raffle.resultsAnimation.winner", {
          name: winner.name,
          prize: formatPrize(winner.prize_amount),
        })
      );
      celebrateWinner(stageCenter);
      setFlyChip(winner);
      await wait(EJECT_DURATION_MS);
      setFlyChip(null);
      setRevealedWinners((current) => [winner, ...current]);
      await wait(700);
    }

    await completeAnimation();
  };

  useEffect(() => {
    if (!autoStart) return undefined;

    const delayMs =
      resultsSeconds !== null && resultsSeconds > 0 ? resultsSeconds * 1000 : 0;
    const timerId = window.setTimeout(() => {
      run();
    }, delayMs);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, resultsSeconds]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearTimers();
      setResultsAnimation({
        activeParticipantIndex: undefined,
        rotation: rotationRef.current,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className={cls.root}>
      {isAnimationStarted && !hasFinishedRef.current && (
        <button
          type="button"
          className={cls.skipButton}
          onClick={skipAnimation}
        >
          {t("raffle.resultsAnimation.skip")}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.46967 10.9697C3.17678 11.2626 3.17678 11.7374 3.46967 12.0303C3.76256 12.3232 4.23744 12.3232 4.53033 12.0303L8.03033 8.53033C8.32322 8.23744 8.32322 7.76256 8.03033 7.46967L4.53033 3.96967C4.23744 3.67678 3.76256 3.67678 3.46967 3.96967C3.17678 4.26256 3.17678 4.73744 3.46967 5.03033L6.43934 8L3.46967 10.9697ZM8.46967 10.9697C8.17678 11.2626 8.17678 11.7374 8.46967 12.0303C8.76256 12.3232 9.23744 12.3232 9.53033 12.0303L13.0303 8.53033C13.3232 8.23744 13.3232 7.76256 13.0303 7.46967L9.53033 3.96967C9.23744 3.67678 8.76256 3.67678 8.46967 3.96967C8.17678 4.26256 8.17678 4.73744 8.46967 5.03033L11.4393 8L8.46967 10.9697Z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
      <div className={cls.stageLayer} ref={stageRef}>
        <canvas ref={canvasRef} className={cls.confetti} aria-hidden="true" />
        {countdownValue && (
          <div className={cls.countdown} key={countdownValue}>
            {countdownValue}
          </div>
        )}
        {flyChip && (
          <div className={cls.flyChip}>
            {flyChip.name} · {formatPrize(flyChip.prize_amount)}
          </div>
        )}
      </div>
      <div className={cls.statusBar}>
        <div className={cls.statusLine}>
          <RaffleTimeIcon />
          {statusText}
        </div>
      </div>
      {results && revealedWinners.length > 0 && (
        <section className={cls.board}>
          <div className={cls.boardHeader}>
            <h2 className={cls.boardTitle}>
              {t("raffle.resultsAnimation.winnersTitle")}
            </h2>
            <p className={cls.boardSubtitle}>
              {t("raffle.resultsAnimation.winnersProgress", {
                current: revealedWinners.length,
                total: results.winners.length,
              })}
            </p>
          </div>
          <div className={cls.winnersList}>
            {revealedWinners.map((winner) => (
              <div
                className={[
                  cls.winnerRow,
                  winner.is_me ? cls.winnerRowMe : "",
                ].join(" ")}
                key={getWinnerKey(winner)}
              >
                <span className={cls.winnerIcon}>
                  <GiftIcon />
                </span>
                <span className={cls.winnerName}>
                  {winner.name}
                  {winner.rides !== undefined && (
                    <small>
                      {t("raffle.leaderboard.orders", { count: winner.rides })}
                    </small>
                  )}
                </span>
                <strong className={cls.winnerPrize}>
                  {formatPrize(winner.prize_amount)}
                </strong>
              </div>
            ))}
          </div>
        </section>
      )}
      {results && (
        <div className={cls.winnersAnnouncer} aria-live="polite">
          {results.winners.map((winner) => (
            <span key={getWinnerKey(winner)}>{winner.name}</span>
          ))}
        </div>
      )}
    </section>
  );
};
